import { ComponentNames as WnCComponentNames } from "../../constant";
import { logAdapterEvent, logError } from "../../helper/logger";
import { getPackageIdsByType } from "../../helper/genre-helper";
import { getComparePkgTrayData } from "../../helper/ComparePkgTray";
import { pickChannels } from "../../helper/channels-service-helpers";
const ADAPTER_NAME = "packageCarouselAdapter";

interface IPackageCarouselAdapter {
  content: any;
  chnlListinPageData: any;
  channelsServiceData?: any;
  index: number;
  cmsPageName?: string;
  packageConfig?: any;
  getOffersData?: any;
  getOffersDTVsData?: any;
  genreSvodOffers?: any;
  svodGenreData?: any;
  embeddedSvods?: any;
  dtviEmbeddedSvodOffers?: any;
  dtvsEmbeddedSvodOffers?: any;
  globalCards?: any[];
}

export const DTVI_IDS = [
  "PREMIER",
  "ULTIMATE",
  "CHOICE",
  "ENTERTAINMENT",
  "OPTIMO MAS"
] as const;
export type DTViId = typeof DTVI_IDS[number];

export const DTVS_TDS = [
  "PREMIER",
  "ULTIMATE",
  "CHOICE",
  "ENTERTAINMENT",
  "OPTIMO MAS",
  "LO MAXIMO",
  "MAS LATINO",
  "MAS ULTRA"
] as const;
export type DTVsTd = typeof DTVS_TDS[number];

const handleGetGenreChannels = (id: string, channels: any[]) => {
  try {
    if (channels) {
      const genreChannels = channels
        ?.filter?.((channel: any) => channel?.packageListSystem?.includes(id))
        .map((channel: any) => ({
          channelNameLabel: channel?.channelNameLabel,
          channelLogoPath: channel?.channelLogoPath
        }))
        ?.sort((a, b) => a?.channelNameLabel?.localeCompare(b?.channelNameLabel));
      return genreChannels;
    }
  } catch (error) {
    logError("PackageCarouselAdapter", error, { method: "handleGetGenreChannels" });
    return [];
  }
};

/**
 * @description
 * Get epoch retrieved package id from card id based on productType
 * @params { productType, cardId }
 * @returns Raw package name without prefix (e.g. ATT_TV_, DTV_TV_), for GENRE return as is
 */
const getEpochId = (productType: string, cardId: string) => {
  switch (productType?.toUpperCase()) {
    case "DTVI":
    case "BYOD":
      return cardId
        ?.replace(/^ATT_TV_/, "")
        ?.split("_")
        ?.join(" ");
    case "DTVS":
      return cardId
        ?.replace(/^DTV_TV_/, "")
        ?.replace(/^DTV_/, "")
        ?.split("_")
        ?.join(" ");
    default:
      return cardId;
  }
};

/**
 * @description
 * Get epoch retrieved package id from card id based on productType
 * @params { productType, cardId }
 * @returns Raw package name without prefix (e.g. ATT_TV_, DTV_TV_), for GENRE return as is
 */
const getEpochIdForSvods = (productType: string, cardId: string) => {
  switch (productType?.toUpperCase()) {
    case "DTVI":
    case "BYOD":
      return cardId
        ?.replace(/^ATT_TV_/, "")
        ?.split("_")
        ?.join("");
    case "DTVS":
      return cardId
        ?.replace(/^DTV_TV_/, "")
        ?.split("_")
        ?.join("");
    default:
      return cardId;
  }
};

/**
 * @description
 * Get package details from packageConfig based on cards config id
 * @params { cards, packageConfig }
 * @returns packageName, channelCount, fallBackPrice for every card
 */
const getPackageDetails = (cards, packageConfig) => {
  try {
    const packageIds = cards?.map(card => card?.config?.id);
    const packageMapping = {};
    packageConfig?.forEach(pkg => {
      if (pkg?.id && packageIds.includes(pkg?.id)) {
        packageMapping[pkg?.id] = {
          packageName: pkg?.displayLabel,
          channelCount: pkg?.channel?.displayCount,
          fallBackPrice: pkg?.price,
          queryparam: pkg?.queryparam || null,
          pkgType: pkg?.type
        };
      }
    });
    return packageMapping;
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "Error getting package details: getPackageDetails"
    });
    return {};
  }
};

// Generated by Copilot- Start - 2026-04-21 16:31:55
/**
 * @description
 * Build a flat id-to-type lookup map from packageConfig
 * @params { packageConfig }
 * @returns Record<string, string> mapping pkg.id → pkg.type
 */
const getIdToTypeMap = (packageConfig: any[]): Record<string, string> => {
  try {
    if (!Array.isArray(packageConfig)) {
      return {};
    }
    return packageConfig.reduce((map, pkg) => {
      if (pkg?.id && pkg?.type) {
        map[pkg.id] = pkg.type;
      }
      return map;
    }, {} as Record<string, string>);
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "Error building id-to-type map: getIdToTypeMap"
    });
    return {};
  }
};

/**
 * @description
 * Filter globalCards based on content.config variants.
 * Both excludeCards and defaultOverrideConfig may co-occur and are applied sequentially:
 *   1. excludeCards (string | string[]): remove cards whose id matches the excludeCards value(s)
 *   2. defaultOverrideConfig: keep only cards matching intent types and remove the intent card by id
 *   If neither is present, returns cards as-is.
 * @params { cards, config, packageConfig }
 * @returns filtered cards array (empty array if cards is not a valid array)
 */
const applyGlobalCardsFilter = ({
  cards,
  config,
  packageConfig
}: {
  cards: any[];
  config: any;
  packageConfig: any[];
}): any[] => {
  try {
    if (!Array.isArray(cards)) {
      return [];
    }

    let filtered = [...cards];

    // excludeCards case: remove cards by id
    // CMS returns a string when only one card is excluded, array when multiple
    const excludeCards = config?.excludeCards ?
      Array.isArray(config.excludeCards) ? config.excludeCards : [config.excludeCards] :
      [];
    if (excludeCards.length > 0) {
      const excludeSet = new Set(excludeCards);
      filtered = filtered.filter(card => !excludeSet.has(card?.config?.id));
    }

    // defaultOverrideConfig case
    if (config?.defaultOverrideConfig) {
      const { id: intentId, intent } = config.defaultOverrideConfig;

      // default2/3/4: filter by allowed types when intent array is non-empty
      if (Array.isArray(intent) && intent.length > 0) {
        const idToTypeMap = getIdToTypeMap(packageConfig);
        const intentSet = new Set(intent);
        filtered = filtered.filter(card => intentSet.has(idToTypeMap[card?.config?.id]));
      }

      // always remove the intent card itself (applies to default2/3/4)
      if (intentId) {
        filtered = filtered.filter(card => card?.config?.id !== intentId);
      }
    }
    return filtered;
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "Error applying global cards filter: applyGlobalCardsFilter"
    });
    return Array.isArray(cards) ? cards : [];
  }
};
// Generated by Copilot- End - 2026-04-21 16:31:55

/**
 * @description
 * Get dynamic pricing for each card
 * @params { productType, cards, getOffersData, getOffersDTVsData, genreSvodOffers, svodGenreData, embeddedSvods, dtviEmbeddedSvodOffers, dtvsEmbeddedSvodOffers, packageConfig }
 * @returns dynamicPricing for every card
 */
const getDynamicPricing = args => {
  const {
    productType,
    cards,
    getOffersData,
    getOffersDTVsData,
    genreSvodOffers,
    packageConfig
  } = args;
  try {
    // Implementation for getting dynamic pricing
    const priceMapping = {};
    let pricesReduced = {};

    switch (productType?.toUpperCase()) {
      case "GENRE":
        // eslint-disable-next-line no-case-declarations
        const genreIds = getPackageIdsByType({
          type: "genre",
          packageConfig
        });
        // eslint-disable-next-line no-case-declarations
        const genreMiniIds = getPackageIdsByType({
          type: "genre-mini",
          packageConfig
        });
        // eslint-disable-next-line no-case-declarations
        const GENRE_IDS = [...genreIds, ...genreMiniIds];
        pricesReduced = genreSvodOffers?.offerPrices?.reduce((acc, curr) => {
          if(!curr) {
            return acc;
          }
          // Generated by Copilot- Start - 2026-04-12 17:26:15
          Object.keys(curr || {}).forEach(key => {
            if (GENRE_IDS.includes(key)) {
              acc[key] = {
                price: curr[key]?.price ?? null,
                regularPrice: curr[key]?.regularPrice ?? null,
                promoDuration: curr[key]?.promoDuration ?? null
              };
            }
          });
          // Generated by Copilot- End - 2026-04-12 17:26:15
          return acc;
        }, {});
        break;
      case "DTVI":
        pricesReduced = getOffersData?.offerPrices?.reduce((acc, curr) => {
          if(!curr) {
            return acc;
          }
          Object.keys(curr || {}).forEach(key => {
            if (DTVI_IDS.includes(key as DTViId)) {
              acc[key] = curr[key] || null;
            }
          });
          return acc;
        }, {});
        break;
      case "DTVS":
        pricesReduced = getOffersDTVsData?.offerPrices?.reduce((acc, curr) => {
          if(!curr) {
            return acc;
          }
          Object.keys(curr || {}).forEach(key => {
            if (DTVS_TDS.includes(key as DTVsTd)) {
              acc[key] = curr?.localPrice?.[key] || null;
            }
          });
          return acc;
        }, {});
        break;
      case "BYOD":
        pricesReduced = getOffersData?.offerPrices?.reduce((acc, curr) => {
          if(!curr) {
            return acc;
          }
          Object.keys(curr || {}).forEach(key => {
            if (DTVI_IDS.includes(key as DTViId)) {
              acc[key] = curr?.byodPrice?.[key] || null;
            }
          });
          return acc;
        }, {});
        break;
      default:
        break;
    }
    // Map the reduced prices to the card config IDs
    // Generated by Copilot- Start
    // Generated by Copilot- Start - 2026-04-12 17:26:15
    cards.forEach(card => {
      const cardId = card?.config?.id;
      // Extract epoch sent key from id, e.g. ATT_TV_CHOICE => CHOICE
      const epochId = getEpochId(productType, cardId);

      const val = pricesReduced?.[epochId];
      const priceVal = typeof val === "object" && val !== null ? val?.price : val;
      const regularVal = typeof val === "object" && val !== null ? val?.regularPrice : undefined;
      const promoDurationVal = typeof val === "object" && val !== null ? val?.promoDuration : undefined;
      priceMapping[cardId] = {
        dynamicPricing:
          Number(priceVal) >= 0 ? String(priceVal) : null,
        ...(regularVal !== undefined && priceVal !== regularVal ? {
          regularPrice: Number(regularVal) >= 0 ? String(regularVal) : null
        } : {}),
        ...(promoDurationVal != null ? { promoDuration: promoDurationVal } : {})
      };
    });
    // Generated by Copilot- End - 2026-04-12 17:26:15
    return priceMapping;
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "Error getting dynamic pricing: getDynamicPricing"
    });
    return [];
  }
};

/**
 * @description
 * Get SVODs for each card
 * @params { productType, cards, getOffersData, getOffersDTVsData, genreSvodOffers, svodGenreData, embeddedSvods, dtviEmbeddedSvodOffers, dtvsEmbeddedSvodOffers }
 * @returns svods for every card
 */
const getDynamicSvods = args => {
  const {
    productType,
    cards,
    genreSvodOffers,
    embeddedSvods,
    dtviEmbeddedSvodOffers,
    dtvsEmbeddedSvodOffers
  } = args;
  try {
    // Implementation for getting dynamic pricing and SVODs
    const svodsMapping = {};
    let svodsReduced = [];
    switch (productType?.toUpperCase()) {
      case "GENRE": {
        // Precompute lookup map: offerCode -> array of matching embedded svods (supports duplicates)
        const embeddedSvodsMap: Record<string, any[]> = {};
        embeddedSvods?.forEach(item => {
          if (item?.offerCode) {
            if (!embeddedSvodsMap[item.offerCode]) {
              embeddedSvodsMap[item.offerCode] = [];
            }
            embeddedSvodsMap[item.offerCode].push(item);
          }
        });

        cards.forEach(card => {
          const cardId = card?.config?.id;
          const svodMapping = genreSvodOffers?.svodMapping?.[cardId];

          svodsReduced =
            svodMapping
              ?.reduce((acc, curr) => {
                // curr is a string each iteration, example -> "BOLT-ESPNPLUS-202411"
                // Expand every embedded svod that shares the same offerCode so CMS duplicates can render separately.
                const matchingSvods = embeddedSvodsMap[curr] || [];

                if (matchingSvods.length > 0) {
                  matchingSvods.forEach(embeddedSvodMatch => {
                    const svod = {
                      code: curr,
                      name:
                        embeddedSvodMatch?.displayName ||
                        genreSvodOffers?.svodNameMapping?.[curr] ||
                        embeddedSvodMatch?.name ||
                        null,
                      logo:
                        embeddedSvodMatch?.packageCarouselData?.icon || null,
                      disable:
                        embeddedSvodMatch?.packageCarouselData?.disable ?? null
                    };

                    acc.push(svod);
                  });
                  return acc;
                }

                const fallbackSvod = {
                  code: curr,
                  name: genreSvodOffers?.svodNameMapping?.[curr] || null,
                  logo: null,
                  disable: null
                };

                acc.push(fallbackSvod);
                return acc;
              }, [])
              ?.filter?.(svod => {
                const hasName = !!svod?.name;
                const hasLogo = !!svod?.logo;
                const isExcluded = card?.excludedSvods?.includes(svod?.code);
                const passes = hasName && hasLogo && !isExcluded && !svod?.disable;

                return passes;
              }) || [];

          svodsMapping[cardId] = {
            svods: svodsReduced || []
          };
        });
        break;
      }
      case "DTVI":
        cards.forEach(card => {
          const epochId = getEpochIdForSvods(productType, card?.config?.id);
          svodsReduced =
            dtviEmbeddedSvodOffers?.[epochId?.toLowerCase()]
              ?.reduce((acc, curr) => {
                // curr is an object each iteration, example -> { code: "BOLT-ESPNPLUS-202411", name: "ESPN Unlimited" }
                const embeddedSvod = embeddedSvods?.find(
                  item => item?.offerCode === curr?.code
                );
                if (embeddedSvod?.packageCarouselData?.disable) {
                  return acc;
                }
                const svod = {
                  code: curr?.code || null,
                  name: curr?.name || null,
                  logo: embeddedSvod?.packageCarouselData?.icon || null
                };
                acc.push(svod);
                return acc;
              }, [])
              ?.filter?.(
                svod => svod?.name &&
                  svod?.logo &&
                  !card?.excludedSvods?.includes(svod?.code)
              ) || [];
          svodsMapping[card?.config?.id] = {
            svods: svodsReduced || []
          };
        });
        break;
      case "BYOD":
        cards.forEach(card => {
          const epochId = getEpochIdForSvods(productType, card?.config?.id);
          svodsReduced =
            dtviEmbeddedSvodOffers?.[epochId?.toLowerCase()]
              ?.reduce((acc, curr) => {
                // curr is an object each iteration, example -> { code: "BOLT-ESPNPLUS-202411", name: "ESPN Unlimited" }
                const embeddedSvod = embeddedSvods?.find(
                  item => item?.offerCode === curr?.code
                );
                if (embeddedSvod?.packageCarouselData?.disable) {
                  return acc;
                }
                const svod = {
                  code: curr?.code || null,
                  name: curr?.name || null,
                  logo: embeddedSvod?.packageCarouselData?.icon || null
                };
                acc.push(svod);
                return acc;
              }, [])
              ?.filter?.(
                svod => svod?.name &&
                  svod?.logo &&
                  !card?.excludedSvods?.includes(svod?.code)
              ) || [];
          svodsMapping[card?.config?.id] = {
            svods: svodsReduced || []
          };
        });
        break;
      case "DTVS":
        cards.forEach(card => {
          const epochId = getEpochIdForSvods(productType, card?.config?.id);
          svodsReduced =
            dtvsEmbeddedSvodOffers?.[epochId?.toLowerCase()]
              ?.reduce((acc, curr) => {
                // curr is an object each iteration, example -> { code: "BOLTON-ESPN-UNLIMITED_satellite", name: "ESPN Unlimited" }
                const embeddedSvod = embeddedSvods?.find(
                  item => item?.offerCodeSatellite === curr?.code
                );
                if (embeddedSvod?.packageCarouselData?.disable) {
                  return acc;
                }
                const svod = {
                  code: curr?.code || null,
                  name: curr?.name || null,
                  logo: embeddedSvod?.packageCarouselData?.icon || null
                };
                acc.push(svod);
                return acc;
              }, [])
              ?.filter?.(
                svod => svod?.name &&
                  svod?.logo &&
                  !card?.excludedSvods?.includes(svod?.code)
              ) || [];
          svodsMapping[card?.config?.id] = {
            svods: svodsReduced || []
          };
        });
        break;
      default:
        break;
    }
    return svodsMapping;
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "Error getting dynamic pricing and SVODs: getDynamicSvods"
    });
    return [];
  }
};

/**
 * @description
 * - Inject Dynamic[EPOCH] Offer Price,
 * - Inject Dynamic SVODS based on productType [DTVI, DTVS, BYOD, GENRE]
 * - Inject Dynamic[CMS] packageName, channelCount, fallBackPrice
 * @params { productType, cards, packageConfig, getOffersData, getOffersDTVsData, genreSvodOffers, svodGenreData, embeddedSvods, dtviEmbeddedSvodOffers, dtvsEmbeddedSvodOffers }
 * @returns new cards with Dynamic Injected Data
 */
const injectDynamicContent = args => {
  const {
    productType,
    cards,
    packageConfig,
    getOffersData,
    getOffersDTVsData,
    genreSvodOffers,
    embeddedSvods,
    dtviEmbeddedSvodOffers,
    dtvsEmbeddedSvodOffers,
    disableSvods
  } = args;
  try {
    // Implementation for injecting dynamic content into cards
    const dynamicPackageData = getPackageDetails(cards, packageConfig);
    const dynamicPricing = getDynamicPricing({
      productType,
      cards,
      getOffersData,
      getOffersDTVsData,
      genreSvodOffers,
      packageConfig
    });
    const dynamicSvods = getDynamicSvods({
      productType,
      cards,
      genreSvodOffers,
      embeddedSvods,
      dtviEmbeddedSvodOffers,
      dtvsEmbeddedSvodOffers
    });

    return cards?.map(card => {
      // Resolve pricing context once per card
      const cardId = card?.config?.id;
      const cp = dynamicPricing?.[cardId];
      const pkg = dynamicPackageData?.[cardId];
      const price = cp?.dynamicPricing ?? pkg?.fallBackPrice ?? "";
      const hasPromo = !!(cp?.regularPrice && cp?.dynamicPricing && cp.dynamicPricing !== cp.regularPrice);

      // Resolve card-level priceText duration based on promo state
      // hasPromo + promoDuration → replace token; otherwise → clear duration so spanText renders instead
      let resolvedDuration: string | undefined = undefined;
      if (card?.priceText?.duration) {
        if (hasPromo && cp?.promoDuration != null) {
          resolvedDuration = card.priceText.duration.replace?.("[promo_duration]", String(cp.promoDuration));
        }
        // else: resolvedDuration stays undefined → duration cleared, component falls back to spanText
      }

      // Resolve modal duration independently — front-card and modal can have
      // different templates (or only one can be authored). Same promo logic.
      let resolvedModalDuration: string | undefined = undefined;
      if (card?.modalData?.priceText?.duration) {
        if (hasPromo && cp?.promoDuration != null) {
          resolvedModalDuration = card.modalData.priceText.duration.replace?.("[promo_duration]", String(cp.promoDuration));
        }
      }

      return {
        ...card,
        title: {
          ...card?.title,
          content: card?.title?.content?.replace("[package_name]", pkg?.packageName || "")
        },
        queryparam: pkg?.queryparam || null,
        pkgType: pkg?.pkgType || null,
        subHeading: {
          ...card?.subHeading,
          content: card?.subHeading?.content?.replace("[channel_count]", pkg?.channelCount || "")
        },
        // Card-level strikeThroughPrice — show only when promo active
        ...(card?.strikeThroughPrice && {
          strikeThroughPrice: hasPromo ?
            {
              ...card.strikeThroughPrice,
              content: card.strikeThroughPrice.content?.replace("[regular_price]", cp.regularPrice)
            } :
            {
              ...card.strikeThroughPrice,
              content: undefined
            }
        }),
        // Card-level offerRibbon — show only when promo active (mirrors modalData.offerRibbon)
        ...(card?.offerRibbon && {
          offerRibbon: hasPromo
            ? card.offerRibbon
            : { ...card.offerRibbon, content: undefined }
        }),
        // Card-level priceText with dynamic offer_price and duration
        priceText: {
          ...card?.priceText,
          content: card?.priceText?.content?.replace("[offer_price]", price),
          ...(card?.priceText?.duration !== undefined ? { duration: resolvedDuration } : {})
        },
        // Modal dynamic pricing — same hasPromo drives strikeThroughPrice/offerRibbon visibility
        ...(card?.modalData?.priceText?.content && {
          modalData: {
            ...card.modalData,
            ...(card.modalData.strikeThroughPrice && {
              strikeThroughPrice: hasPromo ?
                {
                  ...card.modalData.strikeThroughPrice,
                  content: card.modalData.strikeThroughPrice.content?.replace("[regular_price]", cp.regularPrice)
                } :
                {
                  ...card.modalData.strikeThroughPrice,
                  content: undefined
                }
            }),
            ...(card.modalData.offerRibbon && {
              offerRibbon: hasPromo ?
                card.modalData.offerRibbon :
                {
                  ...card.modalData.offerRibbon,
                  content: undefined
                }
            }),
            priceText: {
              ...card.modalData.priceText,
              content: card.modalData.priceText.content.replace("[offer_price]", price),
              ...(card.modalData.priceText.spanText?.content && {
                spanText: {
                  ...card.modalData.priceText.spanText,
                  content: card.modalData.priceText.spanText.content.replace("[offer_price]", price)
                }
              }),
              // Mirror front-card duration behavior: when promo active → resolved duration; otherwise cleared so component falls back to spanText.
              ...(card.modalData.priceText.duration !== undefined ? { duration: resolvedModalDuration } : {})
            }
          }
        }),
        svods: !disableSvods ? dynamicSvods?.[cardId]?.svods : []
      };
    });
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      index: args?.index,
      cms_type: args?.cms_type,
      cmsPageName: args?.cmsPageName,
      step: "Error injecting dynamic data into GENRE cards: injectDynamicContent"
    });
    return cards;
  }
};

export const packageCarouselAdapter = (args: IPackageCarouselAdapter) => {
  try {
    logAdapterEvent(ADAPTER_NAME, "Adapter execution started", {
      index: args?.index,
      cmsPageName: args?.cmsPageName
    });

    let { content } = args;
    const {
      chnlListinPageData,
      channelsServiceData,
      packageConfig,
      getOffersData,
      getOffersDTVsData,
      genreSvodOffers,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      svodGenreData,
      embeddedSvods,
      dtviEmbeddedSvodOffers,
      dtvsEmbeddedSvodOffers,
      globalCards
    } = args;
    const channels =
      pickChannels(
        channelsServiceData?.channels,
        chnlListinPageData?.[":items"]?.chnlListingPageData?.elements?.channels
      );

    // Generated by Copilot- Start - 2026-04-24 19:00:14
    // Override cards with globalCards and apply variant-based filtering
    // Clone content.cards to avoid mutating the shared cached Storyblok object
    if (content?.config?.useGlobalCards && Array.isArray(globalCards)) {
      content = {
        ...content,
        cards: applyGlobalCardsFilter({
          cards: globalCards,
          config: content.config,
          packageConfig
        })
      };
    }
    // Generated by Copilot- End - 2026-04-24 19:00:14

    // Add Genre Channels into Mini Packs
    content.cards = (content.cards || []).map((card: any) => {
      if (card?.config?.type === "modal") {
        return {
          ...card,
          modalData: {
            ...card.modalData,
            channels: handleGetGenreChannels(card?.config?.id, channels)
          }
        };
      }
      return card;
    });

    // Add MyFree Channels into myFreeConfig
    if (content?.cards?.find((card: any) => card?.config?.type === "modal")) {
      content.myFreeConfig = {
        ...content.myFreeConfig,
        channels: handleGetGenreChannels("MYFREE_DTV", channels)
      };
    }
    /** --------------------------------------------------- */
    /** Add Dynamic Content if productType is present */
    if (content?.config?.productType) {
      content.cards = injectDynamicContent({
        productType: content?.config?.productType,
        cards: content?.cards,
        packageConfig,
        getOffersData,
        getOffersDTVsData,
        genreSvodOffers,
        embeddedSvods,
        dtviEmbeddedSvodOffers,
        dtvsEmbeddedSvodOffers,
        disableSvods: content?.config?.disableSvods ?? false
      });

      // Generated by Copilot- Start - 2026-04-12 17:26:15
      // AC2: Compute min price from base pack cards only (exclude genre-mini) and replace [min_price] in topper priceText
      try {
        if (content?.priceText?.content?.includes?.("[min_price]")) {
          const basePacks = content.cards?.filter(card => card?.pkgType !== "genre-mini");
          const cardPrices = basePacks
            ?.map(card => {
              const raw = card?.priceText?.content;
              // Strip currency symbols & units (e.g. "$45.99/mo" → "45.99") so parseFloat gets a clean decimal
              const num = parseFloat(raw?.replace?.(/[^0-9.]/g, ""));
              return isNaN(num) ? null : num;
            })
            ?.filter(p => p !== null);
          const minPrice = cardPrices?.length > 0 ? Math.min(...cardPrices) : null;
          if (minPrice !== null) {
            content.priceText = {
              ...content.priceText,
              content: content.priceText.content.replace(
                "[min_price]",
                String(minPrice)
              )
            };
          }
        }
      } catch (e) {
        logError(ADAPTER_NAME, e, { step: "Error computing min price for topper" });
      }
      // Generated by Copilot- End - 2026-04-12 17:26:15
    }
    const comparePkgTrayData = content?.comparePkgTray ? getComparePkgTrayData({
      comparePkgTray: content.comparePkgTray,
      packageConfig,
      genreSvodOffers,
      getOffersData
    }) : null;
    return {
      props: {
        ...content,
        comparePkgTray: comparePkgTrayData ? comparePkgTrayData : null
      },
      type: content?.type
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      index: args?.index
    });
    return {
      props: {
        error: error?.message,
        type: WnCComponentNames.PACKAGECAROUSEL,
        options: {
          id: `ERROR-package-carousel-${args?.index}`
        }
      }
    };
  }
};
